/**
 * VM-M6: typed client for Expenses + Attachments + Categories.
 *
 * Two surfaces:
 *   - submitter: /expenses/mine, POST /expenses, attachments,
 *                info-response
 *   - accountant: /expenses (queue), approve/reject/request-info,
 *                 mark-reimbursed
 */
import { apiFetch } from '@/lib/api';

const BASE = '/mod/vm';

export type ExpenseKind = 'receipt' | 'invoice' | 'credit_memo';
export type ReconciliationStatus =
  | 'pending' | 'info_requested' | 'approved'
  | 'rejected' | 'reconciled';
export type ReimbursementStatus = 'not_applicable' | 'pending' | 'paid';
export type MatchStatus = 'unmatched' | 'partial' | 'variance' | 'matched';
export type ExpenseTransitionAxis = 'reconciliation' | 'reimbursement';

export interface Expense {
  id: string;
  kind: ExpenseKind;
  vendor_id: string;
  procurement_request_id: string | null;
  purchase_order_id: string | null;
  category_id: string | null;
  classification_id: string | null;
  transaction_date: string;
  amount_cents: number;
  currency: string;
  description: string | null;
  merchant_name: string | null;
  invoice_number: string | null;
  reconciliation_status: ReconciliationStatus;
  reconciled_at: string | null;
  reconciled_by: string | null;
  rejection_reason: string | null;
  info_request_note: string | null;
  info_response_note: string | null;
  info_responded_at: string | null;
  is_reimbursable: boolean;
  reimbursement_status: ReimbursementStatus;
  reimbursed_at: string | null;
  reimbursed_by: string | null;
  reimbursement_method: string | null;
  reimbursement_external_ref: string | null;
  gl_account_hint: string | null;
  cost_center: string | null;
  project_code: string | null;
  is_capitalizable: boolean;
  match_status: MatchStatus;
  amount_variance_pct: string | number | null;
  match_recomputed_at: string | null;
  submitted_by: string;
  needs_qbo_sync: boolean;
  created_at: string;
  updated_at: string;
}

export interface ExpenseAttachment {
  id: string;
  expense_id: string;
  filename: string;
  content_type: string;
  size_bytes: number;
  checksum_sha256: string;
  storage_key: string;
  uploaded_by: string | null;
  created_at: string;
}

export interface ExpenseTransition {
  id: string;
  expense_id: string;
  from_status: string | null;
  to_status: string;
  axis: ExpenseTransitionAxis;
  actor_user_id: string | null;
  reason: string | null;
  comment: string | null;
  metadata_json: Record<string, unknown> | null;
  occurred_at: string;
}

export interface ExpenseCategory {
  id: string;
  slug: string;
  name: string;
  description: string | null;
  default_gl_account_hint: string | null;
  default_is_capitalizable: boolean;
  is_system: boolean;
  is_active: boolean;
  sort_order: number;
}

// ── Expense CRUD ──────────────────────────────────────────────────

export interface ExpenseCreatePayload {
  kind: ExpenseKind;
  vendor_id: string;
  transaction_date: string;
  amount_cents: number;
  currency?: string;
  description?: string;
  merchant_name?: string;
  invoice_number?: string;
  procurement_request_id?: string;
  purchase_order_id?: string;
  category_id?: string;
  classification_id?: string;
  is_reimbursable?: boolean;
  is_capitalizable?: boolean;
  gl_account_hint?: string;
  cost_center?: string;
  project_code?: string;
}

export const createExpense = (body: ExpenseCreatePayload) =>
  apiFetch<Expense>(`${BASE}/expenses`, {
    method: 'POST', body: JSON.stringify(body),
  });

export const listMyExpenses = (params?: {
  status?: ReconciliationStatus;
  kind?: ExpenseKind;
  limit?: number;
  offset?: number;
}) => {
  const p = new URLSearchParams();
  if (params?.status) p.set('status', params.status);
  if (params?.kind) p.set('kind', params.kind);
  if (params?.limit) p.set('limit', String(params.limit));
  if (params?.offset) p.set('offset', String(params.offset));
  const qs = p.toString();
  return apiFetch<Expense[]>(`${BASE}/expenses/mine${qs ? '?' + qs : ''}`);
};

export const listExpenseQueue = (params?: {
  status?: ReconciliationStatus;
  kind?: ExpenseKind;
  vendor_id?: string;
  has_pr?: boolean;
  match_status?: MatchStatus;
  min_amount_cents?: number;
  max_amount_cents?: number;
  submitted_by?: string;
  limit?: number;
  offset?: number;
}) => {
  const p = new URLSearchParams();
  if (params?.status) p.set('status', params.status);
  if (params?.kind) p.set('kind', params.kind);
  if (params?.vendor_id) p.set('vendor_id', params.vendor_id);
  if (params?.has_pr !== undefined) p.set('has_pr', String(params.has_pr));
  if (params?.match_status) p.set('match_status', params.match_status);
  if (params?.min_amount_cents !== undefined) p.set('min_amount_cents', String(params.min_amount_cents));
  if (params?.max_amount_cents !== undefined) p.set('max_amount_cents', String(params.max_amount_cents));
  if (params?.submitted_by) p.set('submitted_by', params.submitted_by);
  if (params?.limit) p.set('limit', String(params.limit));
  if (params?.offset) p.set('offset', String(params.offset));
  const qs = p.toString();
  return apiFetch<Expense[]>(`${BASE}/expenses${qs ? '?' + qs : ''}`);
};

export const getExpense = (id: string) =>
  apiFetch<Expense>(`${BASE}/expenses/${id}`);

export const listExpenseTransitions = (id: string) =>
  apiFetch<ExpenseTransition[]>(`${BASE}/expenses/${id}/transitions`);

export const approveExpense = (id: string, body: {
  gl_account_hint?: string;
  cost_center?: string;
  project_code?: string;
  classification_id?: string;
  is_capitalizable?: boolean;
  comment?: string;
}) => apiFetch<Expense>(`${BASE}/expenses/${id}/approve`, {
  method: 'POST', body: JSON.stringify(body),
});

export const rejectExpense = (id: string, body: { reason: string; comment?: string }) =>
  apiFetch<Expense>(`${BASE}/expenses/${id}/reject`, {
    method: 'POST', body: JSON.stringify(body),
  });

export const requestInfo = (id: string, note: string) =>
  apiFetch<Expense>(`${BASE}/expenses/${id}/request-info`, {
    method: 'POST', body: JSON.stringify({ note }),
  });

export const respondToInfoRequest = (id: string, note: string) =>
  apiFetch<Expense>(`${BASE}/expenses/${id}/info-response`, {
    method: 'POST', body: JSON.stringify({ note }),
  });

export const markReimbursed = (id: string, body: {
  reimbursement_method?: string;
  reimbursement_external_ref?: string;
}) => apiFetch<Expense>(`${BASE}/expenses/${id}/mark-reimbursed`, {
  method: 'POST', body: JSON.stringify(body),
});

export const deleteExpense = (id: string) =>
  apiFetch<void>(`${BASE}/expenses/${id}`, { method: 'DELETE' });

// ── Attachments ───────────────────────────────────────────────────

export const uploadAttachment = (expense_id: string, file: File) => {
  const fd = new FormData();
  fd.append('file', file);
  return apiFetch<ExpenseAttachment>(
    `${BASE}/expenses/${expense_id}/attachments`,
    { method: 'POST', body: fd },
  );
};

export const listAttachments = (expense_id: string) =>
  apiFetch<ExpenseAttachment[]>(`${BASE}/expenses/${expense_id}/attachments`);

export const attachmentContentUrl = (expense_id: string, attachment_id: string) =>
  `/api/v1${BASE}/expenses/${expense_id}/attachments/${attachment_id}/content`;

export const deleteAttachment = (expense_id: string, attachment_id: string) =>
  apiFetch<void>(`${BASE}/expenses/${expense_id}/attachments/${attachment_id}`, {
    method: 'DELETE',
  });

// ── Categories ────────────────────────────────────────────────────

export const listCategories = (include_inactive = false) =>
  apiFetch<ExpenseCategory[]>(
    `${BASE}/expense-categories${include_inactive ? '?include_inactive=true' : ''}`,
  );

export const createCategory = (body: {
  slug: string;
  name: string;
  description?: string;
  default_gl_account_hint?: string;
  default_is_capitalizable?: boolean;
  sort_order?: number;
}) => apiFetch<ExpenseCategory>(`${BASE}/expense-categories`, {
  method: 'POST', body: JSON.stringify(body),
});

export const updateCategory = (id: string, body: {
  name?: string;
  description?: string;
  default_gl_account_hint?: string;
  default_is_capitalizable?: boolean;
  is_active?: boolean;
  sort_order?: number;
}) => apiFetch<ExpenseCategory>(`${BASE}/expense-categories/${id}`, {
  method: 'PUT', body: JSON.stringify(body),
});

export const deleteCategory = (id: string) =>
  apiFetch<void>(`${BASE}/expense-categories/${id}`, { method: 'DELETE' });

// ── Display helpers ───────────────────────────────────────────────

export function money(cents: number, currency = 'USD'): string {
  return new Intl.NumberFormat(undefined, {
    style: 'currency', currency, maximumFractionDigits: 2,
  }).format(cents / 100);
}

export const RECONCILIATION_LABEL: Record<ReconciliationStatus, string> = {
  pending: 'Pending',
  info_requested: 'Info Requested',
  approved: 'Approved',
  rejected: 'Rejected',
  reconciled: 'Reconciled',
};

export const REIMBURSEMENT_LABEL: Record<ReimbursementStatus, string> = {
  not_applicable: 'N/A',
  pending: 'Pending payment',
  paid: 'Paid',
};

export const MATCH_LABEL: Record<MatchStatus, string> = {
  unmatched: 'Unmatched',
  partial: 'Partial',
  variance: 'Variance',
  matched: 'Matched',
};
